/**
 * "Your booths" — the signed-in creator's projects as a card gallery under
 * the composer (Google Opal-style: cover image, name + meta on the card
 * top). Clicking a card opens the live booth.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaSpinner, FaCamera } from 'react-icons/fa'
import { useCopy } from '../regionCopy'
import { authHeaders } from '../onboardingApi'
import { publicBoothHostLabel } from '../publicOrigin'

const API_BASE = (): string => process.env.VITE_API_URL || 'https://dreamboothstudio.com'

interface BoothProject {
  _id: string
  title: string
  thumbnail?: string
  slug?: string
  updatedAt?: string
}

interface MyBoothsProps {
  signedIn: boolean
}

function timeAgo(iso?: string): string {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`
}

export const MyBooths: React.FC<MyBoothsProps> = ({ signedIn }) => {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<BoothProject[] | null>(null)
  const [loading, setLoading] = useState(false)
  const c = useCopy()

  useEffect(() => {
    if (!signedIn) {
      setProjects(null)
      return
    }
    let cancelled = false
    setLoading(true)
    // authHeaders is what actually authenticates on dreambooth.app — the
    // studio cookie is first-party to the studio, not to this origin, so
    // without the bearer token this 401'd and "Your booths" rendered null
    // for every signed-in creator on the consumer domain.
    fetch(`${API_BASE()}/api/projects`, {
      credentials: 'include',
      cache: 'no-cache',
      headers: authHeaders()
    })
      .then(async (res) => (res.ok ? ((await res.json()) as BoothProject[]) : null))
      .then((data) => {
        if (cancelled || !data) return
        const sorted = [...data].sort(
          (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
        )
        setProjects(sorted.slice(0, 12))
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [signedIn])

  if (!signedIn || (!loading && (!projects || projects.length === 0))) return null

  return (
    <section className="mx-auto w-full max-w-4xl px-6 pb-16">
      <h2 className="mb-4 text-sm font-medium uppercase tracking-wide text-whiteout/40">
        {c.yourBooths}
      </h2>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-whiteout/40">
          <FaSpinner className="animate-spin text-xl" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {projects!.map((p) => (
            <button
              key={p._id}
              type="button"
              onClick={() => navigate(`/${p.slug || p._id}`)}
              className="group relative aspect-[3/4] overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.03] text-left shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:border-white/20"
            >
              {p.thumbnail ? (
                <img
                  src={p.thumbnail}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-80 transition-opacity duration-300 group-hover:opacity-100"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-whiteout/20">
                  <FaCamera className="text-3xl" />
                </div>
              )}
              {/* Top scrim + meta (Opal-style: identity on top of the card) */}
              <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-3">
                <div className="truncate text-sm font-semibold text-whiteout">{p.title}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-whiteout/60">
                  {p.slug && <span className="truncate">{publicBoothHostLabel()}/{p.slug}</span>}
                  <span className="shrink-0">{timeAgo(p.updatedAt)}</span>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 flex justify-end p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="rounded-full bg-blueSparkle px-3 py-1 text-[10px] font-semibold text-whiteout">
                  {c.openBooth}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

export default MyBooths
